import { faShop } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import React, { useMemo, useState } from "react"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef } from "material-react-table"
import { CompanySettingsCustomJobQuery, fetchCustomJobs, FetchCustomJobsResponse } from "../customJobApi"
import { MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveCompanyId } from "../../../../../context/contextSelectors"
import {
    CustomJob,
    CustomReportsType,
} from "../../../../../merchant/accounting/accounting/merchantCustomReports/customReportsTypes"
import { updateCustomJob } from "../../../../../merchant/accounting/accounting/merchantCustomReports/customReportsApi"
import { StandardRunCustomJobDialog } from "../../../../../merchant/accounting/accounting/merchantCustomReports/components/StandardRunCustomJobDialog"

type Props = Readonly<{}>

const useStyles = makeStyles()((theme) => ({
    activateText: {
        color: theme.palette.tailwind.green[600],
    },
    inactivateText: {
        color: theme.palette.tailwind.red[600],
    },
}))

const transformData = (data: FetchCustomJobsResponse) => {
    return data.customJobs
}

export function CompanyCustomJobs({}: Props) {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const [showRunJobDialog, setShowRunJobDialog] = useState(false)
    const [selectedCustomJob, setSelectedCustomJob] = useState<CustomJob>()
    const companyId = useAppSelector(selectEffectiveCompanyId)

    const customJobs = useApiQuery(fetchCustomJobs, {
        queryName: CompanySettingsCustomJobQuery.CUSTOM_JOBS_LIST,
        dependencies: {
            companyId: companyId ?? 0,
            type: CustomReportsType.IMPORT,
        },
        enabled: Boolean(companyId),
        select: transformData,
    })

    const { mutate: callUpdateCustomJob } = useApiMutation(updateCustomJob, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [CompanySettingsCustomJobQuery.CUSTOM_JOBS_LIST],
            })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(customJobs)

    const columns = useMemo<MRT_ColumnDef<CustomJob>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("company-admin-settings-custom-jobs-table-name"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("company-admin-settings-custom-jobs-table-interval"),
                accessorKey: "interval",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("company-admin-settings-custom-jobs-table-last-run"),
                    accessorKey: "lastRunTime",
                },
                (row) => row?.lastRunTime,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("company-admin-settings-custom-jobs-table-status"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <LocalizedStrict id="company-admin-settings-custom-jobs-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_SETTINGS_CUSTOM_JOBS}
                    title="Custom jobs"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-settings">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-custom-jobs">
                            <Breadcrumb>Custom jobs</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{
                    density: "spacious",
                }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="company-admin-settings-custom-jobs-table-action-details">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                //todo
                            }}
                            key={`details-${row.original.id}`}
                        >
                            Details
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="company-admin-settings-custom-jobs-table-action-run-job">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                setSelectedCustomJob(row.original)
                                setShowRunJobDialog(true)
                            }}
                            key={`run-job-${row.original.id}`}
                        >
                            Run job
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict
                        id={
                            row.original.isActive
                                ? "company-admin-settings-custom-jobs-table-action-inactivate"
                                : "company-admin-settings-custom-jobs-table-action-activate"
                        }
                    >
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                callUpdateCustomJob({
                                    jobId: row.original.id,
                                    isActive: !row.original.isActive,
                                })
                            }}
                            className={row.original.isActive ? classes.inactivateText : classes.activateText}
                            key={`status-${row.original.id}`}
                        >
                            {row.original.isActive ? "Inactivate" : "Activate"}
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {showRunJobDialog && selectedCustomJob && (
                <StandardRunCustomJobDialog
                    item={selectedCustomJob}
                    open={showRunJobDialog}
                    onClose={() => setShowRunJobDialog(false)}
                />
            )}
        </>
    )
}
